import { Suspense, useRef } from "react";
import { useForceSidebarIntoMode } from "~/page-layouts/sidebar-layout";
import { Helmet } from "react-helmet-async";
import { NotFound } from "~/components/NotFound";
import { ListScrollbox } from "~/components/list";
import * as ThreadLayout from "~/page-layouts/thread-layout";
import { useTopScrollShadow } from "~/hooks/useScrollShadow";
import { ICommandArgs, useRegisterCommands, withNewCommandContext } from "~/environment/command.service";
import { slate } from "@radix-ui/colors";
import { useIsOnline } from "~/hooks/useIsOnline";
import {
  closeViewCommand,
  deleteThreadCommand,
  nextThreadCommand,
  previousThreadCommand,
  restoreThreadCommand,
} from "~/utils/common-commands";
import { useSetWebpageBackgroundColor } from "~/hooks/useSetWebpageBackgroundColor";
import { ThreadTimeline } from "./ThreadTimeline";
import { closeThreadView } from "./utils";
import { ThreadHeader } from "./ThreadHeader";
import { ActionToolbar } from "./ActionToolbar";
import { useThread } from "~/hooks/useThread";
import { useThreadFound } from "~/hooks/useIsThreadFound";
import { usePreloadThreadData } from "~/hooks/usePreloadThreadData";
import { withOfflineFirstIfSynced } from "~/components/withOfflineFirstIfSynced";
import { useClientEnvironment } from "~/environment/ClientEnvironmentContext";
import { useNotification } from "~/hooks/useNotification";
import { ThreadLoading } from "./ThreadLoading";
import { ThreadPermissionDenied } from "./ThreadPermissionDenied";
import { cx } from "@emotion/css";
import { RecordValue } from "libs/schema";
import { useUserProfile } from "~/hooks/useUserProfile";
import { OutlineButton } from "~/components/OutlineButtons";
import { deleteThread, restoreThread } from "~/actions/thread";
import { useShowThreadInfoPanel } from "~/hooks/useShowThreadInfoPanel";
import { ProvideThreadInfoPanelContext, ThreadInfoPanel } from "./ThreadInfoPanel";
import { ParentComponent } from "~/utils/type-helpers";
import { useParams } from "@tanstack/react-router";
import { useThreadViewContext } from "./context";
import { moveToEntry } from "./RegisterThreadTimelineCommands";

/* -------------------------------------------------------------------------------------------------
 * ThreadView
 * -----------------------------------------------------------------------------------------------*/

export const ThreadView = withNewCommandContext({
  priority: { delta: 2 },
  Component: withOfflineFirstIfSynced({
    useIsOfflineFirst: () => {
      const params = useParams({ strict: false });
      const [notification] = useNotification({
        threadId: params.threadId,
        fetchStrategy: "cache",
      });
      return !!notification && !notification.is_done;
    },
    Component: () => {
      const params = useParams({ strict: false });
      const scrollboxRef = useRef<HTMLElement>(null);
      const headerRef = useRef<HTMLElement>(null);
      const isAppOnline = useIsOnline();
      const threadId = params.threadId;

      useSetWebpageBackgroundColor(slate.slate3);

      useRegisterThreadViewCommands({ threadId });

      useForceSidebarIntoMode("over");

      const [showInfoPanel] = useShowThreadInfoPanel();

      const { threadFound } = useThreadFound(threadId);
      const { isThreadLoading } = usePreloadThreadData({ threadId });
      const [thread] = useThread(threadId, { includeSoftDeletes: true });
      const isThreadDeleted = !!thread?.deleted_at;

      useTopScrollShadow({
        scrollboxRef,
        targetRef: headerRef,
        deps: [isThreadLoading, threadFound],
      });

      if (!threadId) {
        return <NotFound title="Thread Not Found" />;
      }

      if (threadFound !== "found" && !isThreadLoading) {
        if (threadFound === "permission-denied") {
          return <ThreadPermissionDenied threadId={threadId} />;
        }

        if (!isAppOnline) {
          return <NotFound title="App Offline" />;
        }

        return <NotFound title="Thread Not Found" />;
      }

      return (
        <Suspense fallback={<ThreadLoading />}>
          <ProvideThreadInfoPanelContext>
            <div className="MainPanel absolute top-0 left-0 w-full h-full flex bg-slate-3" style={{ zIndex: 2 }}>
              <ListScrollbox ref={scrollboxRef} offsetTopPx={isThreadDeleted ? 42 : 0}>
                <div className="flex-1 h-full overflow-auto flex flex-col">
                  <ThreadViewHeader threadId={threadId} headerRef={headerRef} />

                  <div className="flex-1 flex">
                    <ThreadLayout.ActionPanel>
                      <ActionToolbar threadId={threadId} />
                    </ThreadLayout.ActionPanel>

                    <ThreadLayout.ContentPanel>
                      {isThreadLoading ?
                        <ThreadLoading />
                      : <ThreadTimeline threadId={threadId} />}
                    </ThreadLayout.ContentPanel>

                    <div className="flex-1" />
                  </div>
                </div>
              </ListScrollbox>

              {showInfoPanel && <ThreadInfoPanel threadId={threadId} />}
            </div>
          </ProvideThreadInfoPanelContext>
        </Suspense>
      );
    },
  }),
});

/* -----------------------------------------------------------------------------------------------*/

const ThreadViewHeader: ParentComponent<{
  threadId: string;
  headerRef: React.RefObject<HTMLElement>;
}> = (props) => {
  const [thread] = useThread(props.threadId, { includeSoftDeletes: true });

  return (
    <>
      <Helmet>
        <title>{thread?.subject || ""} | Thread | Comms</title>
      </Helmet>

      {thread?.deleted_at && <ThreadDeletedBanner thread={thread} />}

      <ThreadLayout.Header
        ref={props.headerRef}
        theme={thread?.visibility === "PRIVATE" ? "dark" : "light"}
        // "marginTop" ensures that child elements account for the parents position while "top" ensures that the
        // header itself accounts for the deleted banner if the header is sticking to the top.
        style={{
          marginTop: thread?.deleted_at ? "42px" : undefined,
          top: thread?.deleted_at ? "42px" : undefined,
        }}
      >
        <ThreadLayout.ActionPanel />

        <ThreadLayout.ContentPanel>
          <ThreadHeader threadId={props.threadId} />
        </ThreadLayout.ContentPanel>

        <div className="flex-1" />
      </ThreadLayout.Header>
    </>
  );
};

/* -----------------------------------------------------------------------------------------------*/

const ThreadDeletedBanner: ParentComponent<{
  thread: Pick<RecordValue<"thread">, "id" | "deleted_at" | "deleted_by_user_id">;
}> = (props) => {
  const [user] = useUserProfile(props.thread.deleted_by_user_id);

  return (
    <div
      className={cx(
        "fixed top-0 w-full z-30 flex justify-center items-center transition-shadow duration-300 bg-red-9",
        "text-white font-medium py-1",
      )}
    >
      Thread deleted{user ? ` by ${user.name}` : ""}
      <OutlineButton onClick={() => restoreThreadCommand.trigger()} className="border-white ml-2 scale-75">
        Restore
      </OutlineButton>
    </div>
  );
};

/* -----------------------------------------------------------------------------------------------*/

/**
 * These commands will be available to someone on the thread
 * view even if the thread hasn't loaded.
 */
function useRegisterThreadViewCommands(props: { threadId: string | null | undefined }) {
  const { threadId } = props;
  const environment = useClientEnvironment();
  const [thread] = useThread(threadId, { includeSoftDeletes: true });
  const threadContext = useThreadViewContext();

  useRegisterCommands({
    commands: () => {
      const commands: ICommandArgs[] = [
        closeViewCommand({
          callback() {
            closeThreadView(environment, threadContext.threadList);
          },
        }),
        previousThreadCommand({
          callback: () => {
            moveToEntry(environment, {
              direction: "previous",
              threadViewContext: threadContext,
            });
          },
        }),
        nextThreadCommand({
          callback: () => {
            moveToEntry(environment, {
              direction: "next",
              threadViewContext: threadContext,
            });
          },
        }),
      ];

      if (thread) {
        if (thread?.deleted_at) {
          commands.push(
            restoreThreadCommand({
              callback: () => {
                restoreThread(environment, { threadId: thread.id });
              },
            }),
          );
        } else {
          commands.push(
            deleteThreadCommand({
              callback: () => {
                deleteThread(environment, { threadId: thread.id });
              },
            }),
          );
        }
      }

      return commands;
    },
    deps: [thread, threadContext, environment],
  });
}

/* -----------------------------------------------------------------------------------------------*/
